import React from 'react';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router';

// Types
import { WorkspaceMeeting } from 'Shared/types/WorkspaceMeeting.type';

// Utils
import { bodyWithoutDescription } from 'Shared/components/DraftEditor/utils';
import { createRichTextEditorState } from 'Wpds/components/rich-text/utils/EditorState.utils';
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';
import { formatDate } from 'Shared/utils/DateTime.utils';

// Helpers
import { editorStateForEditorComponent } from 'Shared/utils/draftJsHelpers';

// Selectors
import * as UsersSelectors from 'Shared/selectors/Users.selectors';

// Components
import { WpButton } from 'Wpds/components/button';
import { WpAvatar, WpAvatarEmpty } from 'Wpds/components/avatar';
import DraftEditor from 'Shared/components/DraftEditor/DraftEditor';
import SnapshotComments from './SnapshotComments';
import SnapshotMeetingItemCheckmarkIcon from './SnapshotMeetingItemCheckmarkIcon';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';

// Styles
import './SnapshotMeetingItem.scss';

type Props = {
  snapshot;
  meetingItem;
  subItem?;
  workspaceMeeting: WorkspaceMeeting;
};

const SnapshotMeetingItem = ({ snapshot, meetingItem, subItem, workspaceMeeting }: Props) => {
  const history = useHistory();
  const { workspaceId } = useParams();

  const { id, uuid: itemUuid, isSection, body, subItems = [] } = meetingItem;

  const meetingItemAssignment =
    snapshot.data.meetingItemAssignments.find(assignment => assignment.meetingItemId === id) || {};

  const { userId: assigneeId = null } = meetingItemAssignment;

  const assignee = useSelector(state => UsersSelectors.getUserByUserId(state, { userId: assigneeId }));

  const comments = snapshot.data.workspaceComments
    .filter(comment => comment.workspaceItemId === id)
    .map(comment => {
      return { ...comment, authorId: comment.userId };
    });
  const hasComments = comments.length > 0;
  const hasSubItems = subItems.length > 0;

  const assigneeAvatar = assignee?.toJS().avatar;

  const date = workspaceMeeting.startTime;
  const currentYear = new Date().getFullYear();
  const isCurrentYear = currentYear.toString() === formatDate(date, 'YYYY');
  const formattedDate = formatDate(date, `MMMM Do${isCurrentYear ? '' : ', YYYY'}`);

  const bodyToRender = isSection
    ? createRichTextEditorState(bodyWithoutDescription(body))
    : editorStateForEditorComponent(body);

  const handleDetailsClick = () => {
    history.push(PageUrlsUtils.makeWorkspaceHistoryAgendaItemUrl({ workspaceId, itemUuid }));
  };

  const baseClassName = 'SnapshotMeetingItem';
  const clsses = classnames(
    baseClassName,
    isSection && `${baseClassName}--section`,
    subItem && `${baseClassName}--sub-item`
  );

  return (
    <div className={clsses}>
      <div className={`${baseClassName}__header`}>
        {!isSection && (
          <div className={`${baseClassName}__header__avatar`}>
            {assigneeAvatar ? <WpAvatar avatar={assigneeAvatar} size="sm" /> : <WpAvatarEmpty size="sm" />}
          </div>
        )}
        {!isSection && (
          <>
            <div className={`${baseClassName}__header__check-icon`}>
              <SnapshotMeetingItemCheckmarkIcon meetingItem={meetingItem} workspaceMeeting={workspaceMeeting} />
            </div>
          </>
        )}
        <div className={`${baseClassName}__header__body`}>
          <DraftEditor editorState={bodyToRender} readOnly />
        </div>
        {!isSection && (
          <WpButton variant="tertiary" size="sm" onClick={handleDetailsClick}>
            Details
          </WpButton>
        )}
      </div>
      {hasComments && <SnapshotComments comments={comments} />}
      {hasSubItems && (
        <div className={`${baseClassName}__sub-items`}>
          {subItems.map(subItem => (
            <SnapshotMeetingItem
              key={`${itemUuid}-subitem-${subItem.uuid}`}
              snapshot={snapshot}
              meetingItem={subItem}
              subItem
              workspaceMeeting={workspaceMeeting}
            />
          ))}
        </div>
      )}
      {isSection && !hasSubItems && (
        <div className={`${baseClassName}__sub-items__empty-state`}>
          <EmptyStateContainer subTitle={`This section was empty on ${formattedDate}`} grayBackground />
        </div>
      )}
    </div>
  );
};

export default SnapshotMeetingItem;
